import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Building2,
  CheckCircle2,
  Send,
  ArrowRight,
} from 'lucide-react'
import { usePageTitle } from '@/hooks/usePageTitle'
import { LocalizedLink } from '@/components/LocalizedLink'
import { ScrollReveal, SectionHeading, CtaBanner, PageHero, PartnerScroller } from '@/components/ui'
import type { Partner } from '@/components/ui'

interface FormErrors {
  name?: string
  email?: string
  message?: string
  privacy?: string
}

export default function Contacts() {
  const { t } = useTranslation()
  usePageTitle('contactsPage.meta.title', 'contactsPage.meta.description')

  const phone = t('contact.phone')
  const email = t('contact.email')

  const partners: Partner[] = [
    { name: 'Mengon Legnami', logo: '/media/suppliers/mengon.jpg' },
    { name: 'Cris and Cris Colors', logo: '/media/suppliers/crisandcris.jpg' },
    { name: 'Impronte', logo: '/media/suppliers/impronte.jpg' },
    { name: 'Edilceramica', logo: '/media/suppliers/edilceramica.jpg', href: 'mailto:ledilceramica@fastpiu.it' },
    { name: 'Rossini', logo: '/media/suppliers/rossini.jpg', href: 'mailto:rossinicava@fastpiu.it' },
    { name: 'Techno', logo: '/media/suppliers/techno.jpg', href: 'http://www.tscrema.com/' },
    { name: 'Luma', logo: '/media/suppliers/luma.jpg', href: 'mailto:luma.snc@tiscali.it' },
    { name: 'Ferla', logo: '/media/suppliers/ferla.jpg', href: 'https://www.ferlaservice.it/' },
    { name: 'PLG', logo: '/media/suppliers/plg.jpg', href: 'http://plg.it/home/hp.aspx' },
    { name: 'Fratus', logo: '/media/suppliers/fratus.png' },
    { name: 'Allocchio', logo: '/media/suppliers/allocchio.jpg', href: 'mailto:robiallocchio@libero.it' },
    { name: 'Pandini Davide', logo: '/media/suppliers/pandini.png', href: 'mailto:davide.pandini@alice.it' },
    { name: 'LG Impianti', logo: '/media/suppliers/lg_impianti.png', href: 'mailto:lg_impianti@alice.it' },
    { name: 'Sipe', logo: '/media/suppliers/sipe.jpg' },
    { name: 'Bianchi Claudio e Grana', logo: '/media/suppliers/bianchi_claudio_e_grana.jpg', href: 'mailto:bianchiclaudioegrana@libero.it' },
  ]

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    privacy: false,
    honeypot: '',
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitted, setSubmitted] = useState(false)

  const setField = (field: keyof typeof form, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const validate = (): FormErrors => {
    const next: FormErrors = {}
    if (!form.name.trim()) next.name = t('contactsPage.form.errors.required')
    if (!form.email.trim()) {
      next.email = t('contactsPage.form.errors.required')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = t('contactsPage.form.errors.email')
    }
    if (!form.message.trim()) next.message = t('contactsPage.form.errors.required')
    if (!form.privacy) next.privacy = t('contactsPage.form.errors.privacy')
    return next
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Honeypot: silently abort if the hidden field is filled
    if (form.honeypot) return

    const nextErrors = validate()
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }

    const subject = encodeURIComponent(`Richiesta di contatto da ${form.name}`)
    const body = encodeURIComponent(
      `Nome: ${form.name}\nEmail: ${form.email}\nTelefono: ${form.phone || 'Non fornito'}\n\nMessaggio:\n${form.message}`
    )
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`
    setSubmitted(true)
  }

  const infoItems = [
    {
      icon: <MapPin className="h-5 w-5" />,
      label: t('contactsPage.info.addressLabel'),
      value: `${t('contactsPage.info.address')}, ${t('contactsPage.info.city')}`,
      href: `https://maps.google.com/?q=${encodeURIComponent(`${t('contactsPage.info.address')}, ${t('contactsPage.info.city')}`)}`,
      external: true,
    },
    {
      icon: <Phone className="h-5 w-5" />,
      label: t('contactsPage.info.phoneLabel'),
      value: phone,
      href: `tel:${phone.replace(/\D/g, '')}`,
    },
    {
      icon: <Mail className="h-5 w-5" />,
      label: t('contactsPage.info.emailLabel'),
      value: email,
      href: `mailto:${email}`,
    },
    {
      icon: <Clock className="h-5 w-5" />,
      label: t('contactsPage.info.hoursLabel'),
      value: t('contactsPage.info.hours'),
    },
    {
      icon: <Building2 className="h-5 w-5" />,
      label: t('contactsPage.info.vatLabel'),
      value: `${t('contactsPage.info.vat')} — ${t('contactsPage.info.reaLabel')} ${t('contactsPage.info.rea')}`,
    },
  ]

  return (
    <main id="main-content">
      {/* Hero */}
      <PageHero
        title={t('contactsPage.hero.title')}
        subtitle={t('contactsPage.hero.subtitle')}
      />

      {/* Info + Form */}
      <section className="bg-bg py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Info card */}
            <ScrollReveal className="lg:col-span-5">
              <div className="relative bg-surface rounded-2xl p-8 md:p-10 shadow-sm overflow-hidden">
                {/* Structural orange marker — the page's signature detail */}
                <div
                  className="absolute top-0 left-0 w-1.5 h-full bg-brand"
                  aria-hidden="true"
                />
                <div
                  className="absolute top-0 left-0 w-10 h-10 bg-brand"
                  style={{ clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
                  aria-hidden="true"
                />

                <div className="relative">
                  <SectionHeading
                    as="h2"
                    title={t('contactsPage.info.title')}
                    className="mb-8"
                  />

                  <dl className="space-y-6">
                    {infoItems.map((item) => (
                      <div key={item.label} className="flex items-start gap-4">
                        <dt className="flex-shrink-0 p-2.5 rounded-lg bg-bg text-brand">
                          {item.icon}
                        </dt>
                        <dd className="flex-1">
                          <span className="block text-xs font-semibold uppercase tracking-wider text-text-muted">
                            {item.label}
                          </span>
                          {item.href ? (
                            <a
                              href={item.href}
                              target={item.external ? '_blank' : undefined}
                              rel={item.external ? 'noopener noreferrer' : undefined}
                              className="mt-1 inline-block text-heading font-medium hover:text-brand transition-colors"
                            >
                              {item.value}
                            </a>
                          ) : (
                            <span className="mt-1 block text-heading font-medium">
                              {item.value}
                            </span>
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            </ScrollReveal>

            {/* Form card */}
            <ScrollReveal className="lg:col-span-7" delay={0.15}>
              <div className="bg-surface rounded-2xl p-8 md:p-10 shadow-sm">
                <SectionHeading
                  as="h2"
                  title={t('contactsPage.form.title')}
                  className="mb-8"
                />

                {submitted ? (
                  <div
                    className="flex flex-col items-center justify-center gap-4 py-12 text-center"
                    role="status"
                    aria-live="polite"
                  >
                    <div className="p-4 rounded-full bg-green-100 text-green-700">
                      <CheckCircle2 className="h-8 w-8" />
                    </div>
                    <h3 className="font-display text-h3 text-heading">
                      {t('contactsPage.form.successTitle')}
                    </h3>
                    <p className="text-text max-w-md">
                      {t('contactsPage.form.successBody')}
                    </p>
                    <a
                      href={`tel:${phone.replace(/\D/g, '')}`}
                      className="mt-2 inline-flex items-center gap-2 text-brand font-semibold hover:text-brand-dark transition-colors"
                    >
                      {phone}
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} noValidate>
                    {/* Honeypot */}
                    <div className="sr-only" aria-hidden="true">
                      <label htmlFor="website">Website</label>
                      <input
                        id="website"
                        name="website"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.honeypot}
                        onChange={(e) => setField('honeypot', e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-heading mb-1.5">
                          {t('contactsPage.form.nameLabel')}
                        </label>
                        <input
                          id="name"
                          type="text"
                          value={form.name}
                          onChange={(e) => setField('name', e.target.value)}
                          aria-invalid={errors.name ? 'true' : 'false'}
                          aria-describedby={errors.name ? 'name-error' : undefined}
                          className="w-full rounded-lg border border-border bg-bg px-4 py-3 text-heading placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 transition-all"
                          placeholder={t('contactsPage.form.namePlaceholder')}
                        />
                        {errors.name && (
                          <p id="name-error" className="mt-1.5 text-sm text-red-600">
                            {errors.name}
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-heading mb-1.5">
                          {t('contactsPage.form.emailLabel')}
                        </label>
                        <input
                          id="email"
                          type="email"
                          value={form.email}
                          onChange={(e) => setField('email', e.target.value)}
                          aria-invalid={errors.email ? 'true' : 'false'}
                          aria-describedby={errors.email ? 'email-error' : undefined}
                          className="w-full rounded-lg border border-border bg-bg px-4 py-3 text-heading placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 transition-all"
                          placeholder={t('contactsPage.form.emailPlaceholder')}
                        />
                        {errors.email && (
                          <p id="email-error" className="mt-1.5 text-sm text-red-600">
                            {errors.email}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-5">
                      <label htmlFor="phone" className="block text-sm font-semibold text-heading mb-1.5">
                        {t('contactsPage.form.phoneLabel')}
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setField('phone', e.target.value)}
                        className="w-full rounded-lg border border-border bg-bg px-4 py-3 text-heading placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 transition-all"
                        placeholder={t('contactsPage.form.phonePlaceholder')}
                      />
                    </div>

                    <div className="mt-5">
                      <label htmlFor="message" className="block text-sm font-semibold text-heading mb-1.5">
                        {t('contactsPage.form.messageLabel')}
                      </label>
                      <textarea
                        id="message"
                        rows={5}
                        value={form.message}
                        onChange={(e) => setField('message', e.target.value)}
                        aria-invalid={errors.message ? 'true' : 'false'}
                        aria-describedby={errors.message ? 'message-error' : undefined}
                        className="w-full rounded-lg border border-border bg-bg px-4 py-3 text-heading placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 transition-all resize-y"
                        placeholder={t('contactsPage.form.messagePlaceholder')}
                      />
                      {errors.message && (
                        <p id="message-error" className="mt-1.5 text-sm text-red-600">
                          {errors.message}
                        </p>
                      )}
                    </div>

                    <div className="mt-5">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.privacy}
                          onChange={(e) => setField('privacy', e.target.checked)}
                          aria-invalid={errors.privacy ? 'true' : 'false'}
                          aria-describedby={errors.privacy ? 'privacy-error' : undefined}
                          className="mt-1 h-4 w-4 rounded border-border text-brand focus:ring-brand"
                        />
                        <span className="text-sm text-text leading-relaxed">
                          {t('contactsPage.form.privacyLabel')}{' '}
                          <LocalizedLink
                            to="privacy"
                            className="text-brand hover:text-brand-dark underline underline-offset-2"
                          >
                            {t('contactsPage.form.privacyLink')}
                          </LocalizedLink>
                        </span>
                      </label>
                      {errors.privacy && (
                        <p id="privacy-error" className="mt-1.5 text-sm text-red-600">
                          {errors.privacy}
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      className="mt-8 inline-flex items-center gap-2 px-8 py-4 bg-brand text-white font-semibold rounded-lg hover:bg-brand-dark transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
                    >
                      {t('contactsPage.form.submit')}
                      <Send className="h-4 w-4" />
                    </button>
                  </form>
                )}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Map */}
      <section aria-label={t('contactsPage.map.title')}>
        <iframe
          title={t('contactsPage.map.embedTitle')}
          src="https://www.google.com/maps?q=Pierre+Costruzioni+SRL%2C+Via+dei+Cascinotti+15%2F17%2C+26010+Credera+Rubbiano+CR%2C+Italy&output=embed"
          className="block w-full h-[360px] md:h-[460px] border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </section>

      {/* Suppliers */}
      <section className="bg-bg py-16 md:py-24" aria-labelledby="suppliers-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <SectionHeading
              as="h2"
              title={t('contactsPage.suppliers.title')}
              subtitle={t('contactsPage.suppliers.description')}
              align="center"
              className="mb-4 md:mb-6"
            />
          </ScrollReveal>
          <PartnerScroller partners={partners} />
        </div>
      </section>

      {/* CTA */}
      <CtaBanner
        title={t('contactsPage.cta.title')}
        action={
          <a
            href={`tel:${phone.replace(/\D/g, '')}`}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-heading font-semibold rounded-lg hover:bg-bg transition-colors shadow-lg"
          >
            <Phone className="h-5 w-5" />
            {t('contactsPage.cta.button')}
          </a>
        }
      />
    </main>
  )
}
